import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { SparklesIcon } from 'lucide-react';
import AdminBrandContext from '../../contexts/AdminBrandContext';
import PortalBrandContext from '../../contexts/PortalBrandContext';
import { useV2Preview } from './useV2Shell';
import { writeShellView } from './shellView';

/**
 * Admin sidebar entry "New portal (preview)" (Ari, 2026-10-12: let GB admins
 * play with the v2 portal without anything going live for brands).
 *
 * Renders ONLY when `isV2PreviewEnabled(user)` passes (prod: ADMIN or
 * SUPER_ADMIN AND VITE_V2_PREVIEW === 'true'); nobody else gets any DOM.
 * A click switches the shell to v2, carries the brand picked in the admin
 * area (when one is picked and the portal roster has it) over to the v2
 * brand switcher, and opens /home. The v2 sidebar's "Admin" entry and
 * "Classic portal" foot lead back here.
 */
const AdminV2PortalLink: React.FC<{ isCollapsed: boolean }> = ({ isCollapsed }) => {
  const preview = useV2Preview();
  // Read the contexts directly: either provider may be absent (tests, odd mounts).
  const admin = useContext(AdminBrandContext);
  const portal = useContext(PortalBrandContext);
  if (!preview) return null;

  const label = 'New portal (preview)';
  const open = () => {
    const brandId = admin?.selectedBrandId ?? null;
    if (brandId !== null && portal?.brands.some((b) => b.id === brandId)) {
      portal.setSelectedBrandId(brandId);
    }
    writeShellView('v2');
  };

  return (
    <Link
      to="/home"
      data-testid="admin-v2-portal-link"
      aria-label={label}
      title={isCollapsed ? label : undefined}
      onClick={open}
      className={`flex w-full items-center rounded-lg py-2.5 my-0.5 text-xs font-normal text-gray-400 transition-all duration-300 ease-out hover:bg-white/5 hover:text-white ${
        isCollapsed ? 'justify-center px-2' : 'px-4'
      }`}
    >
      <span className="flex w-5 flex-shrink-0 items-center justify-center">
        <SparklesIcon aria-hidden="true" size={16} />
      </span>
      <span className={`whitespace-nowrap transition-all duration-300 ease-out ${isCollapsed ? 'ml-0 w-0 overflow-hidden opacity-0' : 'ml-3 opacity-100'}`}>
        {label}
      </span>
    </Link>
  );
};

export default AdminV2PortalLink;
